import {
  CheckboxCustomEvent,
  IonAvatar,
  IonButton,
  IonButtons,
  IonCheckbox,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPopover,
  IonRow,
  IonSearchbar,
  IonSpinner,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { useQuery } from "@tanstack/react-query";
import { t } from "i18next";
import { filterOutline, listCircleOutline, personCircleOutline, removeCircleOutline } from "ionicons/icons";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { debug } from "../App";
import { AuthenticatedUserContext } from "../contexts/AuthenticatedUserContext";
import { ApiService } from "../services";
import Member from "../types/Member";

const TagFilter = (props: any) => {
  const { isLoading, data, refetch, isPending } = useQuery({
    queryKey: ["tags", "member"],
    queryFn: (context) => {
      return ApiService.get("tags?type=member&section=chat");
    },
    enabled: false, // don't run on initialisation, only run when the modal is made visible
  });
  const [checkedTags, setCheckedTags] = useState<number[]>([]);

  return (
    <>
      <IonButton fill={checkedTags.length ? "solid" : "clear"} size={"small"} id={"toggle-filters"}>
        <IonIcon icon={filterOutline} />
      </IonButton>
      <IonPopover
        trigger={"toggle-filters"}
        onIonPopoverWillPresent={() => {
          refetch();
        }}
      >
        {isLoading || isPending ? (
          <div className={"ion-padding ion-text-center"}>
            <IonSpinner name={"dots"} />
          </div>
        ) : (
          <IonList>
            {data?.data?.map((tag: any) => (
              <IonItem key={tag.id}>
                <IonLabel>{tag.name}</IonLabel>
                <IonCheckbox
                  slot={"end"}
                  checked={checkedTags.includes(tag.name)}
                  onIonChange={(e: CheckboxCustomEvent) => {
                    let newValue;

                    if (e.detail.checked) {
                      newValue = [...checkedTags, tag.name];
                    } else {
                      newValue = checkedTags.filter((id) => id !== tag.name);
                    }

                    setCheckedTags(newValue);
                    props.onChange(newValue);
                  }}
                />
              </IonItem>
            ))}
          </IonList>
        )}
      </IonPopover>
    </>
  );
};

interface SelectMembersModalProps {
  isOpen: boolean;
  next: string;
  onDidDismiss?: () => void;
  onSelected: (selectedMembers: string[]) => void;
  except?: () => Promise<string[]>;
  title: string;
}

export const SelectMembersModal = (props: SelectMembersModalProps) => {
  const modal = useRef<HTMLIonModalElement>(null);
  const [checkedState, setCheckedState] = useState<any>({});
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedTags, setSelectedTags] = useState<number[]>([]);
  const [canFilter] = useState<boolean>(true);
  const { isLoading, data, refetch, isPending } = useQuery({
    queryKey: ["members", searchQuery, selectedTags],
    queryFn: async (context) => {
      debug("Querying for members...");

      let except: string[] = [];

      if (props.except) {
        except = await props.except();
      }

      debug("except", except);

      return ApiService.get(
        "members?orderByAsc=nickname&notMe=true&searchQuery=" +
          encodeURIComponent(searchQuery) +
          "&tags=" +
          encodeURIComponent(selectedTags.join(",")) +
          "&except=" +
          encodeURIComponent(except.join(",") || ""),
      );
    },
    enabled: false, // don't run on initialisation, only run when the modal is made visible
  });

  const authenticatedUser = useContext(AuthenticatedUserContext);
  const authenticatedMember = authenticatedUser.member;

  useEffect(() => {
    if (props.isOpen) {
      debug("SelectMembersModal opened, refetching...");
      refetch();
    }
  }, [props.isOpen, selectedTags, refetch]);

  const hasCheckedMembers = useMemo(() => {
    if (Object.keys(checkedState).length > 0) {
      for (let memberId in checkedState) {
        if (checkedState.hasOwnProperty(memberId)) {
          if (checkedState[memberId] === true) {
            return true;
          }
        }
      }
    }

    return false;
  }, [checkedState]);

  const confirm = () => {
    // filter checkedState to just the users that have been selected
    const selectedMembers = Object.keys(checkedState).filter((id) => checkedState[id]);
    props.onSelected?.(selectedMembers);
    modal.current?.dismiss();
  };

  const checkAll = (state: boolean) => {
    const newCheckedState = { ...checkedState };

    for (let i = 0; i < data.data.length; i++) {
      newCheckedState[data.data[i].id] = state;
    }

    setCheckedState(newCheckedState);
  };

  const selectAll = () => {
    checkAll(true);
  };

  const unselectAll = () => {
    // remove the current data from the selection, but keep anything else that was previously selected
    checkAll(false);
  };

  return (
    <IonModal
      isOpen={props.isOpen}
      onIonModalDidDismiss={() => {
        props.onDidDismiss?.();
      }}
      ref={modal}
      onIonModalWillPresent={() => {
        setCheckedState({});
        setSelectedTags([]); // clear the selected tags when the modal is opened. this will also cause refetch to run
      }}
    >
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={() => modal.current?.dismiss()}>{t("cancel")}</IonButton>
          </IonButtons>
          <IonTitle>{props.title}</IonTitle>
          <IonButtons slot="end">
            <IonButton strong={true} onClick={() => confirm()} disabled={!hasCheckedMembers}>
              {props.next}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <IonToolbar slot={"fixed"} className={"ion-no-padding"}>
          <IonGrid className={"ion-no-padding"}>
            <IonRow>
              <IonCol size={canFilter ? "10" : "12"} className={"ion-no-padding"}>
                <IonSearchbar
                  debounce={500}
                  style={{
                    paddingBottom: 0,
                  }}
                  onIonInput={(e) => {
                    setSearchQuery(e.target.value || "");
                  }}
                ></IonSearchbar>
              </IonCol>
              {canFilter ? (
                <IonCol
                  size={"2"}
                  className={"ion-no-padding"}
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <TagFilter
                    onChange={(selectedTags: number[]) => {
                      debug("TagFilter.onChange", selectedTags);
                      setSelectedTags([...selectedTags]);
                    }}
                  />
                </IonCol>
              ) : (
                ""
              )}
            </IonRow>
          </IonGrid>
        </IonToolbar>
        <div
          style={{
            marginTop: "4em",
          }}
        >
          {isLoading || isPending ? (
            <div className={"ion-padding ion-text-center"}>
              <IonSpinner name={"dots"} />
            </div>
          ) : data?.data?.length === 0 ? (
            <div className={"ion-text-center ion-padding"}>{t("noMembersMatchedYourSearch")}</div>
          ) : (
            <>
              {selectedTags.length ? (
                <IonList inset={true}>
                  <IonItem button={true} detail={false} onClick={selectAll}>
                    <IonIcon icon={listCircleOutline} slot={"start"}></IonIcon>
                    <IonLabel>{t("selectAll")}</IonLabel>
                  </IonItem>
                  <IonItem button={true} detail={false} onClick={unselectAll}>
                    <IonIcon icon={removeCircleOutline} slot={"start"}></IonIcon>
                    <IonLabel>{t("unselectAll")}</IonLabel>
                  </IonItem>
                </IonList>
              ) : (
                ""
              )}

              <IonList inset={true}>
                {data?.data
                  ?.filter((member: Member) => {
                    return member.id !== authenticatedMember!.id;
                  })
                  .map((member: Member) => (
                    <IonItem key={`memberList-${member.id}-${checkedState[member.id]}`}>
                      <IonAvatar slot={"start"}>
                        {member.profile_image_tiny ? <img src={member.profile_image_tiny} alt={member.nickname} /> : <IonIcon icon={personCircleOutline} size={"large"} />}
                      </IonAvatar>
                      <IonCheckbox
                        slot="end"
                        checked={checkedState[member.id]}
                        onIonChange={(e: CheckboxCustomEvent) => {
                          const newCheckedState = { ...checkedState };
                          newCheckedState[member.id] = e.detail.checked;
                          debug("Setting checkedState to", JSON.stringify(newCheckedState));
                          setCheckedState(newCheckedState);
                        }}
                      ></IonCheckbox>
                      <IonLabel>{member.nickname}</IonLabel>
                    </IonItem>
                  ))}
              </IonList>
            </>
          )}
        </div>
      </IonContent>
    </IonModal>
  );
};
